

import { useState, useEffect, useRef } from "react"
import PdfViewer from "./PdfViewer"
import { InlineMath } from 'react-katex';
import "katex/dist/katex.min.css" // Import KaTeX styles

const LatexEditor = () => {
  // State variables
  const [mode, setMode] = useState("easy") // Modes: 'easy' or 'advanced'
  const [latexCode, setLatexCode] = useState("")
  const [pdfUrl, setPdfUrl] = useState(null)
  const [loading, setLoading] = useState(false)
  const [isAiFixing, setIsAiFixing] = useState(false)
  const [compileError, setCompileError] = useState(null)
  const [templates, setTemplates] = useState({})
  const [activeTemplate, setActiveTemplate] = useState("")
  const [selectedText, setSelectedText] = useState("")
  const [templateCategories, setTemplateCategories] = useState([])
  const textareaRef = useRef(null)

  // Fetch templates on component mount
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await fetch("/api/templates")
        if (response.ok) {
          const data = await response.json()
          setTemplates(data.templates)

          // Create template categories
          const categories = {}
          Object.entries(data.templates).forEach(([key, template]) => {
            const type = key.split("-")[0] || "general"
            if (!categories[type]) {
              categories[type] = []
            }
            categories[type].push({ key, ...template })
          })
          setTemplateCategories(Object.entries(categories))

          // Set default template (beamer)
          if (data.templates.beamer && !latexCode) {
            setLatexCode(data.templates.beamer.code)
            setActiveTemplate("beamer")
          }
        }
      } catch (error) {
        console.error("Error fetching templates:", error)
      }
    }

    fetchTemplates()
  }, [])

  // Handle text selection in the textarea
  const handleTextSelection = () => {
    if (textareaRef.current) {
      const start = textareaRef.current.selectionStart
      const end = textareaRef.current.selectionEnd

      if (start !== end) {
        setSelectedText(latexCode.substring(start, end))
      } else {
        setSelectedText("")
      }
    }
  }

  // Compile LaTeX to PDF
  const compileLatex = async () => {
    setLoading(true)
    setCompileError(null)

    try {
      const response = await fetch("/api/compile", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ latexCode }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || "PDF compilation failed")
      }

      const pdfBlob = await response.blob()
      const blobUrl = URL.createObjectURL(pdfBlob)
      setPdfUrl(blobUrl)
    } catch (err) {
      console.error(err)
      setCompileError(err.message)
    } finally {
      setLoading(false)
    }
  }

  // AI Fix function for LaTeX code
  const aiFixLatex = async (textToFix = null) => {
    setIsAiFixing(true)

    try {
      const response = await fetch("/api/ai-latex-fix", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          latexCode,
          selectedText: textToFix || selectedText,
        }),
      })

      if (!response.ok) {
        throw new Error("AI fix failed.")
      }

      const { fixedCode, isPartialFix } = await response.json()

      if (isPartialFix && textareaRef.current) {
        // If fixing a selection, replace just that part
        const start = textareaRef.current.selectionStart
        const end = textareaRef.current.selectionEnd
        const before = latexCode.substring(0, start)
        const after = latexCode.substring(end)

        setLatexCode(before + fixedCode + after)

        // Reset selection
        setSelectedText("")
      } else {
        // Replace the entire code
        setLatexCode(fixedCode)
      }

      // Clear any compile errors after fixing
      setCompileError(null)
    } catch (err) {
      console.error(err)
      alert("Error fixing LaTeX code.")
    } finally {
      setIsAiFixing(false)
    }
  }

  // Load template function
  const loadTemplate = (templateKey) => {
    if (templates[templateKey]) {
      if (
        latexCode.trim() !== "" &&
        latexCode.trim() !== templates[activeTemplate]?.code.trim() &&
        !window.confirm("Loading a new template will replace your current code. Continue?")
      ) {
        return
      }
      setLatexCode(templates[templateKey].code)
      setActiveTemplate(templateKey)
      setCompileError(null)
    }
  }

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-indigo-50 to-blue-50">
      <header className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white p-4 shadow-md">
        <h1 className="text-2xl font-bold">LaTeX Editor</h1>
      </header>

      <div className="flex-grow flex p-4 gap-4 overflow-hidden">
        {/* Left: LaTeX Editor */}
        <div className="w-1/2 flex flex-col bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-indigo-100 p-2">
            <div className="flex rounded-lg overflow-hidden shadow-sm">
              <button
                onClick={() => setMode("easy")}
                className={`w-1/2 py-2 px-4 font-medium transition-all duration-200 ${
                  mode === "easy" ? "bg-indigo-600 text-white" : "bg-white text-indigo-700 hover:bg-indigo-50"
                }`}
              >
                Easy Mode
              </button>
              <button
                onClick={() => setMode("advanced")}
                className={`w-1/2 py-2 px-4 font-medium transition-all duration-200 ${
                  mode === "advanced" ? "bg-blue-600 text-white" : "bg-white text-blue-700 hover:bg-blue-50"
                }`}
              >
                Advanced Mode
              </button>
            </div>
          </div>

          {/* Template selector */}
          <div className="bg-indigo-50 p-3 border-b border-indigo-100">
            <div className="flex items-center">
              <label className="text-indigo-700 font-medium mr-2">Template:</label>
              <select
                className="flex-grow bg-white border border-indigo-200 rounded-md px-2 py-1 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={activeTemplate}
                onChange={(e) => loadTemplate(e.target.value)}
              >
                <option value="" disabled>
                  Select a template
                </option>
                {Object.entries(templates).map(([key, template]) => (
                  <option key={key} value={key}>
                    {template.name}
                  </option>
                ))}
              </select>
            </div>
            {templates[activeTemplate]?.description && (
              <p className="mt-2 text-sm text-indigo-600">{templates[activeTemplate].description}</p>
            )}
          </div>

          <div className="relative flex-grow p-3">
            {/* Textarea */}
            <textarea
              ref={textareaRef}
              className="w-full h-full border border-indigo-200 p-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 font-mono text-gray-800 resize-none"
              value={latexCode}
              onChange={(e) => setLatexCode(e.target.value)}
              onSelect={handleTextSelection}
              placeholder="Enter LaTeX code here..."
              spellCheck="false"
            />

            {/* Show AI Fix button when text is selected */}
            {selectedText && (
              <button
                onClick={() => aiFixLatex()}
                className={`absolute top-6 right-6 bg-blue-600 text-white px-3 py-1.5 text-sm rounded-md shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200 ${isAiFixing ? "opacity-75" : ""}`}
                disabled={isAiFixing}
              >
                {isAiFixing ? (
                  <span className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-1 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Fixing...
                  </span>
                ) : (
                  "Fix Selection"
                )}
              </button>
            )}
          </div>

          {/* Error message and AI fix button */}
          {compileError && (
            <div className="p-3 bg-red-50 border-t border-red-100">
              <div className="flex items-center justify-between">
                <p className="text-red-600 text-sm">{compileError}</p>
                <button
                  onClick={() => aiFixLatex()}
                  className={`bg-blue-600 text-white px-3 py-1.5 text-sm rounded-md shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200 ${isAiFixing ? "opacity-75" : ""}`}
                  disabled={isAiFixing}
                >
                  {isAiFixing ? (
                    <span className="flex items-center">
                      <svg
                        className="animate-spin -ml-1 mr-1 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      AI Fixing...
                    </span>
                  ) : (
                    "AI Fix"
                  )}
                </button>
              </div>
            </div>
          )}

          {mode === "advanced" && (
            <div className="p-3 bg-indigo-50 border-t border-indigo-100">
              <button
                onClick={compileLatex}
                className={`w-full py-2.5 px-4 rounded-lg font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 ${
                  loading
                    ? "bg-blue-400 text-white cursor-wait"
                    : "bg-gradient-to-r from-indigo-600 to-blue-600 text-white hover:from-indigo-700 hover:to-blue-700 focus:ring-blue-500"
                }`}
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Compiling...
                  </span>
                ) : (
                  "Compile to PDF"
                )}
              </button>
            </div>
          )}
        </div>

        {/* Right: Preview */}
        <div className="w-1/2 bg-white rounded-xl shadow-lg overflow-hidden flex flex-col">
          <div className="bg-blue-100 p-3 border-b border-blue-200">
            <h2 className="text-lg font-medium text-blue-800">{mode === "easy" ? "Live Preview" : "PDF Preview"}</h2>
          </div>

          <div className="flex-grow overflow-auto p-4">
            {mode === "easy" ? (
              <div className="p-6 bg-white border border-indigo-100 rounded-lg shadow-inner min-h-full">
                <InlineMath math={latexCode}/>
              </div>
            ) : pdfUrl ? (
              <PdfViewer file={pdfUrl} />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500 italic">
                {loading ? "Generating PDF..." : "Compile your LaTeX to see the PDF preview"}
              </div>
            )}
          </div>
        </div>
      </div>

      <footer className="bg-indigo-800 text-indigo-200 text-center py-2 text-sm">
        LaTeX Editor - Modern and Easy to Use
      </footer>
    </div>
  )
}

export default LatexEditor
